import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Brain, 
  Sparkles, 
  Zap, 
  FileText, 
  Users, 
  Settings, 
  Play, 
  Loader2,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Bot,
  Target,
  Activity,
  Clock,
  TrendingUp,
  BarChart3,
  PieChart,
  MessageSquare,
  Lightbulb,
  Shield,
  Thermometer,
  Gauge,
  Wrench,
  AlertTriangle,
  Info,
  Upload,
  Eye,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { generateAIHazop } from '../../services/operations/aiHazopAPI';
import toast from 'react-hot-toast';
import { getEmployeeAssignedTask } from '../../services/operations/adminAPI';
import { useSelector } from 'react-redux';

const AIHazopPage = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.profile);
  const [drawings, setDrawings] = useState([]);
  const [selectedDrawing, setSelectedDrawing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generationStatus, setGenerationStatus] = useState({});
  const [progress, setProgress] = useState({});
  const [expandedDrawing, setExpandedDrawing] = useState(null);
  
  // Structured drawing data for each drawing
  const [drawingData, setDrawingData] = useState({});
  
  const [aiSettings, setAiSettings] = useState({
    nodeCount: 5,
    worksheetCount: 3,
    includeRecommendations: true,
    riskThreshold: 'medium'
  });

  // Template data for helping users
  const processTemplates = {
    heatExchanger: {
      processDescription: "Heat exchange process between hot and cold streams",
      majorEquipment: "Shell and tube heat exchanger, pumps, control valves",
      operatingConditions: "Hot side: 150°C, 3 bar; Cold side: 25°C, 2 bar",
      knownHazards: "High temperature, pressure buildup, thermal shock",
      safetyControls: "Temperature alarms, pressure relief valves, emergency shutdown"
    },
    reactor: {
      processDescription: "Chemical reaction in continuous stirred tank reactor",
      majorEquipment: "CSTR, agitator, heating/cooling system, feed pumps",
      operatingConditions: "Temperature: 80-120°C, Pressure: 1-5 bar, pH: 6-8",
      knownHazards: "Exothermic reaction, toxic chemicals, fire/explosion risk",
      safetyControls: "Temperature control, pressure relief, gas detection, fire suppression"
    },
    distillation: {
      processDescription: "Separation of components by distillation",
      majorEquipment: "Distillation column, reboiler, condenser, reflux drum",
      operatingConditions: "Column pressure: 1.2 bar, Reboiler temp: 180°C",
      knownHazards: "Flammable vapors, high temperature, pressure variations",
      safetyControls: "Flame arrestors, pressure control, temperature monitoring"
    }
  };

  useEffect(() => {
    fetchDrawings();
  }, []);

  const fetchDrawings = async () => {
    try {
      const response = await getEmployeeAssignedTask(user.id);
      if (response) {
        setDrawings(response.data);
        // Initialize drawing data for each drawing
        const initialData = {};
        response.data.forEach(drawing => {
          initialData[drawing._id] = {
            processDescription: '',
            majorEquipment: '',
            operatingConditions: '',
            knownHazards: '',
            safetyControls: ''
          };
        });
        setDrawingData(initialData);
      }
    } catch (error) {
      console.error('Error fetching drawings:', error);
      toast.error('Failed to fetch drawings');
    }
  };

  const simulateProgress = (drawingId, totalSteps) => {
    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep++;
      const progressPercent = Math.min((currentStep / totalSteps) * 100, 95);
      setProgress(prev => ({ ...prev, [drawingId]: progressPercent }));
      
      if (currentStep >= totalSteps) {
        clearInterval(interval);
      }
    }, 1000);
    
    return interval;
  };

  const handleGenerateAIHazop = async (drawingId) => {
    const data = drawingData[drawingId];
    
    // Validation
    if (!data.processDescription.trim()) {
      toast.error('Please provide a process description');
      return;
    }
    if (!data.majorEquipment.trim()) {
      toast.error('Please specify major equipment');
      return;
    }

    setLoading(true);
    setGenerationStatus(prev => ({ ...prev, [drawingId]: 'generating' }));
    setProgress(prev => ({ ...prev, [drawingId]: 0 }));

    // Calculate total steps for progress simulation
    const totalSteps = aiSettings.nodeCount * (1 + aiSettings.worksheetCount);
    const progressInterval = simulateProgress(drawingId, totalSteps);

    try {
      const response = await generateAIHazop(drawingId, aiSettings, data);
      
      if (response.success) {
        clearInterval(progressInterval);
        setProgress(prev => ({ ...prev, [drawingId]: 100 }));
        setGenerationStatus(prev => ({ ...prev, [drawingId]: 'completed' }));
        
        toast.success(
          `AI HAZOP generated successfully! Created ${response.data.nodesCreated} nodes and ${response.data.worksheetsCreated} worksheets.`,
          { duration: 5000 }
        );
        
        fetchDrawings(); // Refresh the drawings list
      } else {
        clearInterval(progressInterval);
        setGenerationStatus(prev => ({ ...prev, [drawingId]: 'error' }));
        setProgress(prev => ({ ...prev, [drawingId]: 0 }));
        toast.error(response.message || 'Failed to generate AI HAZOP');
      }
    } catch (error) {
      console.error('Error generating AI HAZOP:', error);
      clearInterval(progressInterval);
      setGenerationStatus(prev => ({ ...prev, [drawingId]: 'error' }));
      setProgress(prev => ({ ...prev, [drawingId]: 0 }));
      toast.error('Failed to generate AI HAZOP');
    } finally {
      setLoading(false);
    }
  };

  const applyTemplate = (drawingId, templateKey) => {
    const template = processTemplates[templateKey];
    setDrawingData(prev => ({
      ...prev,
      [drawingId]: { ...template }
    }));
    toast.success(`Applied ${templateKey} template`);
  };

  const updateDrawingData = (drawingId, field, value) => {
    setDrawingData(prev => ({
      ...prev,
      [drawingId]: {
        ...prev[drawingId],
        [field]: value
      }
    }));
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'generating':
        return <Loader2 className="h-5 w-5 animate-spin text-blue-500" />;
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Bot className="h-5 w-5 text-slate-400" />;
    }
  };

  const getRiskThresholdColor = (threshold) => {
    switch (threshold) {
      case 'low': return 'from-green-500 to-emerald-600';
      case 'medium': return 'from-amber-500 to-orange-600';
      case 'high': return 'from-red-500 to-pink-600';
      default: return 'from-blue-500 to-indigo-600';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Modern Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="flex items-center gap-4 mb-4">
            <motion.div 
              whileHover={{ scale: 1.05, rotate: 5 }}
              className="p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl shadow-xl"
            >
              <Brain className="h-8 w-8 text-white" />
            </motion.div>
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                AI HAZOP Generator
              </h1>
              <p className="text-slate-600 text-lg mt-2">Automatically generate comprehensive HAZOP analysis using advanced AI</p>
            </div>
          </div>
          
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8">
            <motion.div 
              whileHover={{ scale: 1.02 }}
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/20"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-xl">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{drawings.length}</p>
                  <p className="text-slate-600 text-sm">Available Drawings</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              whileHover={{ scale: 1.02 }}
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/20"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-xl">
                  <Target className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{aiSettings.nodeCount}</p>
                  <p className="text-slate-600 text-sm">Nodes per Drawing</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              whileHover={{ scale: 1.02 }}
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/20"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-xl">
                  <BarChart3 className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{aiSettings.worksheetCount}</p>
                  <p className="text-slate-600 text-sm">Worksheets per Node</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              whileHover={{ scale: 1.02 }}
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/20"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-xl">
                  <Shield className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800 capitalize">{aiSettings.riskThreshold}</p>
                  <p className="text-slate-600 text-sm">Risk Threshold</p>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* AI Settings Panel */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="relative overflow-hidden mb-8"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-indigo-600/5 rounded-2xl"></div>
          <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-8">
            <div className="flex items-center gap-3 mb-6">
              <motion.div 
                whileHover={{ rotate: 180 }}
                transition={{ duration: 0.3 }}
                className="p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl shadow-lg"
              >
                <Settings className="h-6 w-6 text-white" />
              </motion.div>
              <div>
                <h2 className="text-2xl font-bold text-slate-800">AI Generation Settings</h2>
                <p className="text-slate-600 text-sm mt-1">Configure how AI generates your HAZOP analysis</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-slate-700 font-semibold">
                  <Target className="h-4 w-4 text-blue-500" />
                  Nodes per Drawing
                </label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={aiSettings.nodeCount}
                  onChange={(e) => setAiSettings(prev => ({ ...prev, nodeCount: parseInt(e.target.value) }))}
                  className="w-full bg-white/80 backdrop-blur-sm border border-white/30 rounded-xl p-4 text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 shadow-lg transition-all duration-300"
                />
                <p className="text-xs text-slate-500">Recommended: 3-8 nodes</p>
              </div>

              <div className="space-y-3">
                <label className="flex items-center gap-2 text-slate-700 font-semibold">
                  <FileText className="h-4 w-4 text-emerald-500" />
                  Worksheets per Node
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={aiSettings.worksheetCount}
                  onChange={(e) => setAiSettings(prev => ({ ...prev, worksheetCount: parseInt(e.target.value) }))}
                  className="w-full bg-white/80 backdrop-blur-sm border border-white/30 rounded-xl p-4 text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 shadow-lg transition-all duration-300"
                />
                <p className="text-xs text-slate-500">Recommended: 2-5 worksheets</p>
              </div>

              <div className="space-y-3">
                <label className="flex items-center gap-2 text-slate-700 font-semibold">
                  <Activity className="h-4 w-4 text-amber-500" />
                  Risk Threshold
                </label>
                <select
                  value={aiSettings.riskThreshold}
                  onChange={(e) => setAiSettings(prev => ({ ...prev, riskThreshold: e.target.value }))}
                  className="w-full bg-white/80 backdrop-blur-sm border border-white/30 rounded-xl p-4 text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50 shadow-lg transition-all duration-300"
                >
                  <option value="low">Low Risk Focus</option>
                  <option value="medium">Medium Risk Focus</option>
                  <option value="high">High Risk Focus</option>
                </select>
                <p className="text-xs text-slate-500">Affects severity/likelihood ranges</p>
              </div>

              <div className="space-y-3">
                <label className="flex items-center gap-2 text-slate-700 font-semibold">
                  <Lightbulb className="h-4 w-4 text-indigo-500" />
                  Include Recommendations
                </label>
                <div className="flex items-center h-full pt-2">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={aiSettings.includeRecommendations}
                      onChange={(e) => setAiSettings(prev => ({ ...prev, includeRecommendations: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="w-14 h-8 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-indigo-500 peer-checked:to-purple-600 shadow-lg"></div>
                  </label>
                </div>
                <p className="text-xs text-slate-500">Generate safety recommendations</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Drawings List */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-slate-500/5 to-gray-600/5 rounded-2xl"></div>
          <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 overflow-hidden">
            <div className="p-6 border-b border-white/20 bg-gradient-to-r from-slate-50/50 to-white/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-slate-600 to-gray-700 rounded-xl shadow-lg">
                  <FileText className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">Available Drawings</h2>
                  <p className="text-slate-600 text-sm mt-1">Provide detailed process information and generate AI HAZOP analysis</p>
                </div>
              </div>
            </div>

            {drawings.length === 0 ? (
              <div className="text-center py-16">
                <motion.div 
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="p-6 bg-slate-100 rounded-2xl w-fit mx-auto mb-6"
                >
                  <FileText className="h-16 w-16 text-slate-400 mx-auto" />
                </motion.div>
                <p className="text-slate-500 text-xl mb-2">No drawings available.</p>
                <p className="text-slate-400">Create drawings first to generate AI HAZOP analysis</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-8 p-6">
                <AnimatePresence>
                  {drawings.map((drawing, index) => (
                    <motion.div
                      key={drawing._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ delay: index * 0.1 }}
                      className="group relative overflow-hidden bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 transition-all duration-500"
                    >
                      {/* Status Indicator */}
                      <div className="absolute top-4 right-4 z-10">
                        {getStatusIcon(generationStatus[drawing._id])}
                      </div>

                      {/* Drawing Header */}
                      <div className="p-6 border-b border-white/20">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg">
                              <FileText className="h-6 w-6 text-white" />
                            </div>
                            <div>
                              <h3 className="text-xl font-bold text-slate-800">
                                Drawing No: {drawing.drawingNumber}
                              </h3>
                              <div className="flex items-center gap-4 mt-2">
                                <span className="text-sm text-slate-600">
                                  Chairman: {drawing?.chairman?.firstName} {drawing?.chairman?.lastName}
                                </span>
                                <span className="text-sm text-slate-600">
                                  Nodes: {drawing.nodes?.length || 0}
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          <button
                            onClick={() => setExpandedDrawing(expandedDrawing === drawing._id ? null : drawing._id)}
                            className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
                          >
                            {expandedDrawing === drawing._id ? (
                              <ChevronUp className="h-5 w-5 text-slate-600" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-slate-600" />
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Expandable Content */}
                      <AnimatePresence>
                        {expandedDrawing === drawing._id && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                          >
                            <div className="p-6 space-y-6">
                              {/* Template Selection */}
                              <div className="mb-6">
                                <label className="flex items-center gap-2 text-slate-700 font-semibold mb-3">
                                  <Sparkles className="h-4 w-4 text-purple-500" />
                                  Quick Templates (Optional)
                                </label>
                                <div className="flex flex-wrap gap-2">
                                  {Object.keys(processTemplates).map((templateKey) => (
                                    <button
                                      key={templateKey}
                                      onClick={() => applyTemplate(drawing._id, templateKey)}
                                      className="px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-xl text-sm font-medium transition-colors"
                                    >
                                      {templateKey.charAt(0).toUpperCase() + templateKey.slice(1).replace(/([A-Z])/g, ' $1')}
                                    </button>
                                  ))}
                                </div>
                              </div>

                              {/* Structured Input Fields */}
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Process Description */}
                                <div className="space-y-3">
                                  <label className="flex items-center gap-2 text-slate-700 font-semibold">
                                    <MessageSquare className="h-4 w-4 text-blue-500" />
                                    Process Description *
                                  </label>
                                  <textarea
                                    value={drawingData[drawing._id]?.processDescription || ''}
                                    onChange={(e) => updateDrawingData(drawing._id, 'processDescription', e.target.value)}
                                    placeholder="Describe the main process (e.g., Heat exchange between hot oil and cooling water in a shell-and-tube heat exchanger)"
                                    rows={4}
                                    className="w-full bg-white/80 backdrop-blur-sm border border-white/30 rounded-xl p-4 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 shadow-lg transition-all duration-300 resize-none"
                                  />
                                </div>

                                {/* Major Equipment */}
                                <div className="space-y-3">
                                  <label className="flex items-center gap-2 text-slate-700 font-semibold">
                                    <Wrench className="h-4 w-4 text-emerald-500" />
                                    Major Equipment *
                                  </label>
                                  <textarea
                                    value={drawingData[drawing._id]?.majorEquipment || ''}
                                    onChange={(e) => updateDrawingData(drawing._id, 'majorEquipment', e.target.value)}
                                    placeholder="List key equipment (e.g., Shell-and-tube heat exchanger, centrifugal pumps, control valves, flow meters)"
                                    rows={4}
                                    className="w-full bg-white/80 backdrop-blur-sm border border-white/30 rounded-xl p-4 text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 shadow-lg transition-all duration-300 resize-none"
                                  />
                                </div>

                                {/* Operating Conditions */}
                                <div className="space-y-3">
                                  <label className="flex items-center gap-2 text-slate-700 font-semibold">
                                    <Thermometer className="h-4 w-4 text-amber-500" />
                                    Operating Conditions
                                  </label>
                                  <textarea
                                    value={drawingData[drawing._id]?.operatingConditions || ''}
                                    onChange={(e) => updateDrawingData(drawing._id, 'operatingConditions', e.target.value)}
                                    placeholder="Specify temperature, pressure, flow rates (e.g., Hot side: 150°C, 3 bar, 50 m³/h; Cold side: 25°C, 2 bar, 40 m³/h)"
                                    rows={3}
                                    className="w-full bg-white/80 backdrop-blur-sm border border-white/30 rounded-xl p-4 text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50 shadow-lg transition-all duration-300 resize-none"
                                  />
                                </div>

                                {/* Known Hazards */}
                                <div className="space-y-3">
                                  <label className="flex items-center gap-2 text-slate-700 font-semibold">
                                    <AlertTriangle className="h-4 w-4 text-red-500" />
                                    Known Hazards
                                  </label>
                                  <textarea
                                    value={drawingData[drawing._id]?.knownHazards || ''}
                                    onChange={(e) => updateDrawingData(drawing._id, 'knownHazards', e.target.value)}
                                    placeholder="List potential hazards (e.g., High temperature burns, pressure buildup, thermal shock, equipment failure)"
                                    rows={3}
                                    className="w-full bg-white/80 backdrop-blur-sm border border-white/30 rounded-xl p-4 text-slate-700 focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:border-red-500/50 shadow-lg transition-all duration-300 resize-none"
                                  />
                                </div>

                                {/* Safety Controls */}
                                <div className="space-y-3 lg:col-span-2">
                                  <label className="flex items-center gap-2 text-slate-700 font-semibold">
                                    <Shield className="h-4 w-4 text-indigo-500" />
                                    Existing Safety Controls
                                  </label>
                                  <textarea
                                    value={drawingData[drawing._id]?.safetyControls || ''}
                                    onChange={(e) => updateDrawingData(drawing._id, 'safetyControls', e.target.value)}
                                    placeholder="Describe safety systems (e.g., Temperature alarms, pressure relief valves, emergency shutdown system, fire detection)"
                                    rows={3}
                                    className="w-full bg-white/80 backdrop-blur-sm border border-white/30 rounded-xl p-4 text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 shadow-lg transition-all duration-300 resize-none"
                                  />
                                </div>
                              </div>

                              {/* Progress Bar */}
                              <AnimatePresence>
                                {generationStatus[drawing._id] === 'generating' && (
                                  <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="mb-6"
                                  >
                                    <div className="bg-slate-100 rounded-xl p-4">
                                      <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-medium text-slate-700">
                                          Generating AI HAZOP...
                                        </span>
                                        <span className="text-sm text-slate-600">
                                          {Math.round(progress[drawing._id] || 0)}%
                                        </span>
                                      </div>
                                      <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                                        <motion.div
                                          initial={{ width: 0 }}
                                          animate={{ width: `${progress[drawing._id] || 0}%` }}
                                          transition={{ duration: 0.5 }}
                                          className="h-full bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full"
                                        />
                                      </div>
                                      <div className="flex items-center gap-2 mt-3">
                                        <Loader2 className="h-4 w-4 animate-spin text-purple-500" />
                                        <span className="text-xs text-slate-600">
                                          Creating nodes and worksheets...
                                        </span>
                                      </div>
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>

                              {/* Action Buttons */}
                              <div className="space-y-3">
                                <button
                                  onClick={() => handleGenerateAIHazop(drawing._id)}
                                  disabled={loading && generationStatus[drawing._id] === 'generating'}
                                  className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 disabled:from-slate-400 disabled:to-slate-500 text-white py-3 px-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 transform hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed font-semibold"
                                >
                                  {generationStatus[drawing._id] === 'generating' ? (
                                    <>
                                      <Loader2 className="animate-spin h-5 w-5" />
                                      Generating AI HAZOP...
                                    </>
                                  ) : generationStatus[drawing._id] === 'completed' ? (
                                    <>
                                      <CheckCircle className="h-5 w-5" />
                                      AI HAZOP Generated
                                    </>
                                  ) : (
                                    <>
                                      <Zap className="h-5 w-5" />
                                      Generate AI HAZOP
                                      <ArrowRight className="h-4 w-4" />
                                    </>
                                  )}
                                </button>

                                {generationStatus[drawing._id] === 'completed' && (
                                  <motion.button
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    onClick={() => navigate(`/dashboard/employee/view-nodes/${drawing._id}`)}
                                    className="w-full bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white py-2 px-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 transform hover:scale-105 font-medium"
                                  >
                                    View Generated Nodes
                                    <ArrowRight className="h-4 w-4" />
                                  </motion.button>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>
        </motion.div>

        {/* Info Panel */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-600/5 rounded-2xl"></div>
          <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-8">
            <div className="flex items-center gap-3 mb-6">
              <motion.div 
                whileHover={{ scale: 1.1 }}
                className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg"
              >
                <Sparkles className="h-6 w-6 text-white" />
              </motion.div>
              <div>
                <h3 className="text-2xl font-bold text-slate-800">How AI HAZOP Works</h3>
                <p className="text-slate-600 text-sm mt-1">Advanced AI-powered HAZOP analysis generation</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="text-center"
              >
                <div className="p-4 bg-purple-100 rounded-2xl w-fit mx-auto mb-4">
                  <Brain className="h-8 w-8 text-purple-600" />
                </div>
                <h4 className="font-semibold text-slate-800 mb-2">AI Analysis</h4>
                <p className="text-slate-600 text-sm">Advanced AI analyzes your structured process data and generates comprehensive HAZOP nodes with realistic design intents</p>
              </motion.div>
              
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="text-center"
              >
                <div className="p-4 bg-emerald-100 rounded-2xl w-fit mx-auto mb-4">
                  <Target className="h-8 w-8 text-emerald-600" />
                </div>
                <h4 className="font-semibold text-slate-800 mb-2">Smart Worksheets</h4>
                <p className="text-slate-600 text-sm">Automatically creates detailed worksheets with guide words, deviations, causes, consequences, and safety recommendations</p>
              </motion.div>
              
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="text-center"
              >
                <div className="p-4 bg-amber-100 rounded-2xl w-fit mx-auto mb-4">
                  <Zap className="h-8 w-8 text-amber-600" />
                </div>
                <h4 className="font-semibold text-slate-800 mb-2">Instant Results</h4>
                <p className="text-slate-600 text-sm">Get complete HAZOP analysis in minutes with progress tracking and real-time status updates</p>
              </motion.div>
            </div>

            {/* Additional Features */}
            <div className="mt-8 pt-8 border-t border-white/20">
              <h4 className="text-lg font-semibold text-slate-800 mb-4">Key Features</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-slate-700">Structured data input with templates</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-slate-700">Customizable node and worksheet counts</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-slate-700">Risk threshold configuration</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-slate-700">Automatic safety recommendations</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-slate-700">Real-time progress tracking</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-slate-700">Process-specific AI analysis</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AIHazopPage;